import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { WORKSPACE_SECTIONS } from '@/config/workspaceSections';
import {
  LayoutGrid, Layers, Plus, X, Minus, Maximize2, Search, Columns, AppWindow, MonitorUp, StickyNote,
} from 'lucide-react';

const ADMIN_ROLES = ['admin', 'superadmin'];
const STORAGE_KEY = 'cr_workspace_windows_v1';

// Il workspace multi-finestra usa path relativi same-origin: ogni finestra carica
// una sezione dell'app in un iframe con ?embedded=1 (sidebar/header nascosti).
const withEmbedded = (path) => path + (path.includes('?') ? '&' : '?') + 'embedded=1';

// iter103: apre una sezione in una NUOVA finestra del sistema operativo, posizionandola
// sul secondo schermo se il browser espone la Window Management API (con permesso).
async function openOnSecondScreen(path) {
  const url = withEmbedded(path);
  let features = 'noopener,width=1280,height=800';
  try {
    if ('getScreenDetails' in window) {
      const sd = await window.getScreenDetails();
      const other = sd.screens.find((s) => s !== sd.currentScreen) || sd.currentScreen;
      if (other) features = `noopener,left=${other.availLeft},top=${other.availTop},width=${other.availWidth},height=${other.availHeight}`;
    }
  } catch { /* permesso negato → finestra normale */ }
  window.open(url, '_blank', features);
}

// iter103: sticker agenda always-on-top (Document Picture-in-Picture) con fallback popup.
async function openStickerAgenda() {
  const url = '/agenda-sticker';
  try {
    if ('documentPictureInPicture' in window) {
      const pip = await window.documentPictureInPicture.requestWindow({ width: 340, height: 460 });
      pip.document.body.style.margin = '0';
      const iframe = pip.document.createElement('iframe');
      iframe.src = url;
      iframe.setAttribute('style', 'border:0;width:100%;height:100vh;display:block');
      pip.document.body.appendChild(iframe);
      return;
    }
  } catch { /* fallback */ }
  window.open(url, 'cr-sticker', 'width=340,height=480');
}

let zCounter = 10;

export default function WorkspaceWindows() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [windows, setWindows] = useState([]);
  const [mode, setMode] = useState('float'); // 'float' | 'tile'
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);

  const isEmbedded = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('embedded') === '1';

  // Restore persisted windows
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setWindows(parsed.windows || []);
        setMode(parsed.mode || 'float');
      }
    } catch { /* ignore */ }
  }, []);

  // Persist
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ windows, mode }));
    } catch { /* ignore */ }
  }, [windows, mode]);

  const focusWindow = useCallback((id) => {
    zCounter += 1;
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, z: zCounter } : w)));
  }, []);

  const openSection = useCallback((section) => {
    setLauncherOpen(false);
    setSearch('');
    setWindows((ws) => {
      const existing = ws.find((w) => w.path === section.path);
      if (existing) {
        zCounter += 1;
        return ws.map((w) => (w.id === existing.id ? { ...w, z: zCounter, minimized: false } : w));
      }
      zCounter += 1;
      const offset = ws.length * 32;
      const id = `${section.id}-${Date.now()}`;
      return [...ws, {
        id,
        sectionId: section.id,
        title: section.label,
        path: section.path,
        x: 120 + offset, y: 90 + offset,
        w: 760, h: 520,
        z: zCounter,
        minimized: false,
      }];
    });
  }, []);

  const closeWindow = useCallback((id) => {
    setWindows((ws) => ws.filter((w) => w.id !== id));
  }, []);

  const toggleMinimize = useCallback((id, val) => {
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, minimized: val ?? !w.minimized } : w)));
  }, []);

  const openFull = useCallback((w) => {
    closeWindow(w.id);
    navigate(w.path);
  }, [closeWindow, navigate]);

  // ---- Drag & resize (float mode) ----
  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    setWindows((ws) => ws.map((w) => {
      if (w.id !== d.id) return w;
      if (d.type === 'move') {
        return { ...w, x: Math.max(0, d.origX + dx), y: Math.max(0, d.origY + dy) };
      }
      return { ...w, w: Math.max(320, d.origW + dx), h: Math.max(220, d.origH + dy) };
    }));
  }, []);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    setDragging(false);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
  }, [onPointerMove]);

  const startMove = (e, w) => {
    if (mode !== 'float') return;
    e.preventDefault();
    focusWindow(w.id);
    dragRef.current = { id: w.id, type: 'move', startX: e.clientX, startY: e.clientY, origX: w.x, origY: w.y };
    setDragging(true);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  };

  const startResize = (e, w) => {
    if (mode !== 'float') return;
    e.preventDefault();
    e.stopPropagation();
    focusWindow(w.id);
    dragRef.current = { id: w.id, type: 'resize', startX: e.clientX, startY: e.clientY, origW: w.w, origH: w.h };
    setDragging(true);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  };

  if (isEmbedded || !user || !ADMIN_ROLES.includes(user.role)) return null;

  const visible = windows.filter((w) => !w.minimized);
  const minimized = windows.filter((w) => w.minimized);
  const tileCount = visible.length;
  const tileCols = tileCount <= 1 ? 1 : tileCount <= 4 ? 2 : 3;

  const filteredSections = WORKSPACE_SECTIONS.filter((s) =>
    s.label.toLowerCase().includes(search.toLowerCase()));

  const iframeSrc = (w) => withEmbedded(w.path);

  return (
    <>
      {/* Overlay trasparente durante drag/resize per non perdere il pointer sugli iframe */}
      {dragging && <div className="fixed inset-0 z-[9998]" style={{ cursor: 'grabbing' }} />}

      {/* Container finestre */}
      {visible.length > 0 && (
        <div
          className={mode === 'tile'
            ? 'fixed z-[9990] left-2 right-2 bottom-2 top-16 lg:left-[17rem] grid gap-2 pointer-events-none'
            : 'fixed inset-0 z-[9990] pointer-events-none'}
          style={mode === 'tile' ? { gridTemplateColumns: `repeat(${tileCols}, minmax(0, 1fr))` } : undefined}
          data-testid="workspace-windows-container"
        >
          {visible.map((w) => {
            const floatStyle = mode === 'float'
              ? { left: w.x, top: w.y, width: w.w, height: w.h, zIndex: w.z }
              : {};
            return (
              <div
                key={w.id}
                className={`pointer-events-auto flex flex-col bg-white rounded-lg shadow-2xl border border-slate-300 overflow-hidden ${mode === 'float' ? 'absolute' : 'relative min-h-0'}`}
                style={floatStyle}
                onMouseDown={() => mode === 'float' && focusWindow(w.id)}
                data-testid={`workspace-window-${w.sectionId}`}
              >
                {/* Title bar */}
                <div
                  className="flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-800 text-white select-none"
                  style={{ cursor: mode === 'float' ? 'grab' : 'default' }}
                  onPointerDown={(e) => startMove(e, w)}
                  data-testid={`workspace-window-header-${w.sectionId}`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <AppWindow className="h-3.5 w-3.5 shrink-0 opacity-80" />
                    <span className="text-xs font-semibold truncate">{w.title}</span>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button onPointerDown={(e) => e.stopPropagation()} onClick={() => openFull(w)} className="p-1 rounded hover:bg-white/20" title="Apri a schermo intero" data-testid={`workspace-full-${w.sectionId}`}>
                      <Maximize2 className="h-3.5 w-3.5" />
                    </button>
                    <button onPointerDown={(e) => e.stopPropagation()} onClick={() => toggleMinimize(w.id, true)} className="p-1 rounded hover:bg-white/20" title="Riduci a icona" data-testid={`workspace-min-${w.sectionId}`}>
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <button onPointerDown={(e) => e.stopPropagation()} onClick={() => closeWindow(w.id)} className="p-1 rounded hover:bg-red-500" title="Chiudi" data-testid={`workspace-close-${w.sectionId}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {/* Body iframe */}
                <div className="flex-1 min-h-0 relative bg-slate-50">
                  <iframe
                    src={iframeSrc(w)}
                    title={w.title}
                    className="w-full h-full border-0"
                    data-testid={`workspace-iframe-${w.sectionId}`}
                  />
                </div>
                {/* Resize handle (float only) */}
                {mode === 'float' && (
                  <div
                    onPointerDown={(e) => startResize(e, w)}
                    className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
                    style={{ background: 'linear-gradient(135deg, transparent 50%, #94a3b8 50%)' }}
                    data-testid={`workspace-resize-${w.sectionId}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Barra applicazioni (finestre ridotte) */}
      {minimized.length > 0 && (
        <div className="fixed bottom-2 left-1/2 -translate-x-1/2 z-[9995] flex items-center gap-1.5 bg-slate-900/95 backdrop-blur px-2 py-1.5 rounded-full shadow-lg" data-testid="workspace-taskbar">
          {minimized.map((w) => (
            <button
              key={w.id}
              onClick={() => { toggleMinimize(w.id, false); focusWindow(w.id); }}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs"
              data-testid={`workspace-restore-${w.sectionId}`}
            >
              <AppWindow className="h-3 w-3" /> {w.title}
            </button>
          ))}
        </div>
      )}

      {/* Controlli workspace (FAB) */}
      <div className="fixed bottom-4 right-4 z-[9996] flex flex-col items-end gap-2" data-testid="workspace-controls">
        {windows.length > 0 && (
          <div className="flex items-center gap-1 bg-white rounded-full shadow-lg border border-slate-200 p-1">
            <button
              onClick={() => setMode('float')}
              className={`p-1.5 rounded-full ${mode === 'float' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
              title="Finestre sovrapposte (trascinabili)"
              data-testid="workspace-mode-float"
            ><Layers className="h-4 w-4" /></button>
            <button
              onClick={() => setMode('tile')}
              className={`p-1.5 rounded-full ${mode === 'tile' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
              title="Finestre affiancate (griglia)"
              data-testid="workspace-mode-tile"
            ><Columns className="h-4 w-4" /></button>
          </div>
        )}

        {launcherOpen && (
          <div className="w-72 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="workspace-launcher">
            <div className="p-2 border-b border-slate-100">
              <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-100 rounded-md">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca sezione…"
                  className="bg-transparent text-sm outline-none flex-1"
                  autoFocus
                  data-testid="workspace-launcher-search"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              {filteredSections.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">Nessuna sezione</p>
              ) : filteredSections.map((s) => (
                <div key={s.id} className="flex items-center gap-1 group">
                  <button
                    onClick={() => openSection(s)}
                    className="flex-1 text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-blue-50 flex items-center gap-2"
                    data-testid={`workspace-open-${s.id}`}
                    title="Apri come finestra interna"
                  >
                    <LayoutGrid className="h-3.5 w-3.5 text-slate-400" /> {s.label}
                  </button>
                  <button
                    onClick={() => { setLauncherOpen(false); openOnSecondScreen(s.path); }}
                    className="p-2 rounded-md text-slate-400 hover:bg-slate-100 hover:text-blue-600"
                    data-testid={`workspace-secondscreen-${s.id}`}
                    title="Apri su secondo schermo (nuova finestra)"
                  >
                    <MonitorUp className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* iter103: sticker impegni (always-on-top / PiP) */}
        <button
          onClick={openStickerAgenda}
          className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-800 hover:bg-slate-900 text-white shadow-lg text-xs font-semibold"
          title="Apri lo sticker impegni del giorno (sempre in primo piano)"
          data-testid="workspace-sticker-btn"
        >
          <StickyNote className="h-4 w-4" /> <span className="hidden sm:inline">Sticker impegni</span>
        </button>

        <button
          onClick={() => setLauncherOpen((v) => !v)}
          className="flex items-center gap-2 px-4 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-xl transition-transform hover:scale-105"
          title="Apri una sezione in una finestra affiancata"
          data-testid="workspace-fab"
        >
          {launcherOpen ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          <span className="text-sm font-semibold hidden sm:inline">Finestre</span>
          {windows.length > 0 && (
            <span className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[11px] font-bold rounded-full bg-white text-blue-700" data-testid="workspace-count">
              {windows.length}
            </span>
          )}
        </button>
      </div>
    </>
  );
}
